import React, { useContext, useState } from 'react'
import {
  Button,
  TextField,
  Box,
  InputAdornment,
  IconButton
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { AuthContext } from 'contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { validateEmail } from 'utils/formatUtils'
import { useTranslation } from 'react-i18next'

type FormData = {
  email: string
  password: string
}

const LoginForm = () => {
  const { login } = useContext(AuthContext)
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { t } = useTranslation()

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: { email: '', password: '' }
  })

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    await login(data.email, data.password)
    setIsSubmitting(false)
  }

  return (
    <Box sx={{ maxWidth: '648px', margin: '0 auto' }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name='email'
          control={control}
          rules={{
            required: t('login.emailRequired'),
            validate: validateEmail
          }}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={e => field.onChange(e.target.value.replace(/\s/g, ''))}
              label={t('login.email')}
              fullWidth
              margin='normal'
              disabled={isSubmitting}
              inputProps={{ maxLength: 50 }}
              InputLabelProps={{ shrink: true }}
              error={!!errors.email}
              helperText={errors.email?.message}
              sx={{
                mb: 2,
                '& .MuiInputBase-input': { color: 'black' },
                '& .MuiInputLabel-root': { color: '#7C7C7C' }
              }}
            />
          )}
        />

        <Controller
          name='password'
          control={control}
          rules={{ required: t('login.passwordRequired') }}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={e => field.onChange(e.target.value.replace(/\s/g, ''))}
              label={t('login.password')}
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin='normal'
              disabled={isSubmitting}
              inputProps={{ maxLength: 64 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => setShowPassword(prev => !prev)}
                      edge='end'
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              InputLabelProps={{ shrink: true }}
              error={!!errors.password}
              helperText={errors.password?.message}
              sx={{
                mb: 2,
                '& .MuiInputBase-input': { color: 'black' },
                '& .MuiInputLabel-root': { color: '#7C7C7C' }
              }}
            />
          )}
        />

        <Box sx={{ textAlign: 'left', mb: 2 }}>
          <Button
            variant='text'
            onClick={() => navigate('/password-reset')}
            disabled={isSubmitting}
            sx={{ color: 'secondary.main' }}
          >
            {t('login.forgotPassword')}
          </Button>
        </Box>

        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}
        >
          <Button
            fullWidth
            variant='outlined'
            onClick={() => navigate('/signup')}
            disabled={isSubmitting}
          >
            {t('login.becomeDealer')}
          </Button>

          <Button
            type='submit'
            fullWidth
            variant='contained'
            color='primary'
            disabled={isSubmitting}
          >
            {t('login.loginButton')}
          </Button>
        </Box>
      </form>
    </Box>
  )
}

export default LoginForm
